/**
 * Upgraded to react-router-dom v6 (from v5's Switch/Route-component pattern)
 * specifically so the new customer-facing screens (which all use v6 hooks —
 * useNavigate, useParams, useLocation with a `state` object) can be mounted
 * via <CustomerRoutes />. See routing/CustomerRoutes.jsx for everything that
 * moved out of this file.
 *
 * Mechanical changes from the original:
 *   - `Switch` -> `Routes`
 *   - `<Route exact path="/x" component={Y} />` -> `<Route path="/x" element={<Y />} />`
 *     (the `exact` prop is gone in v6 — all paths are exact-matched by default
 *     unless they end in `/*`, so dropping `exact` everywhere is correct, not
 *     an oversight)
 *   - The final catch-all `render={() => <h2>Not found</h2>}` -> `path="*" element={...}`
 *   - `/customer-login` now renders the new SignIn screen (replacing
 *     CustomerLogin) per direction — SignIn is imported from
 *     `screens/SignIn`, not the old `containers` barrel.
 *   - All customer-only screens (Home, Coupons, Businesses, MyAccount,
 *     Payment, etc.) are NOT listed individually here — they live in
 *     <CustomerRoutes />, mounted once near the bottom.
 *
 * UPDATE: PrivateAdminRoute has been confirmed and fixed for v6 (see
 *   containers/PrivateAdminRoute/PrivateAdminRoute.js). It no longer renders
 *   its own nested <Route> — it just returns <Component /> or a redirect —
 *   so `element={<PrivateAdminRoute component={X} />}` below is exactly the
 *   right shape; no further changes needed here.
 *
 * BLOGS ROUTING FIX: this file used to also register `/blogs` and
 * `/blogs/:id` directly, using leftover v5 syntax
 * (`<Route exact path="..." component={WebsiteBlogs} />`) that v6 doesn't
 * recognize at all — v6 only reads `element`, so that Route matched the
 * path but rendered nothing (a blank page). Worse, since v6 ranks routes by
 * specificity rather than source order, that broken literal `/blogs` match
 * won over this file's own `/*` catch-all below — which is what actually
 * renders <CustomerRoutes />, where `/blogs` and `/blogs/:id` are ALSO
 * registered correctly (with `element`, wrapped in CustomerLayout for the
 * header). So the correct version in customerRoutes.js was never even
 * reached. Removed the two broken lines and the now-unused WebsiteBlogs
 * import here — customerRoutes.js is the single source of truth for
 * `/blogs` now.
 *
 * 404 FIX: the trailing `path="*"` fallback below is dead code — it ties in
 * specificity with the `path="/*"` route just above it (both are splat
 * routes matching any path), and v6 breaks specificity ties by source
 * order, so `/*` -> <CustomerRoutes /> always wins first. Swapped its
 * element to <NotFound /> for consistency, but the real fix for
 * customer-facing 404s is a matching catch-all inside CustomerRoutes.jsx,
 * since that's what actually receives every non-literal path.
 */
import 'react-toastify/dist/ReactToastify.css';
import BusinessSessionGuard from 'containers/BusinessSessionGuard/BusinessSessionGuard';

import React, { useCallback, useEffect, useState } from 'react';
import './index.css';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Slide, ToastContainer } from 'react-toastify';
import { initializeBranch } from 'service/branchService';
import NavBar from '../../containers/Dashboard/Components/NavBar';

import {
  AnalyticsDashboard,
  DealerAgreement,
  FlyerCouponsTest,
  GroupProtected,
  Landing,
  NavigationLoadingOverlay,
  PrivacyPolicy,
  RakutenCouponFetcher,
  ScrollToTop,
  SplashScreen,
  TermsOfService,
} from 'components';
import {
  AccountSettings,
  AdminPanel,
  AffiliateBusinessManager,
  AffiliateCouponViewer,
  BlogManager,
  BrainTreePaymentsPage,
  CJOfferFetcher,
  CustomerCouponListAll,
  Dashboard,
  AuthFlow,
  // CouponBuilder removed from here
  Dashboard2,
  Dashboard3,
  PremiumDashboardContainer,
  ForgotPassword,
  ImpactOfferFetcher,
  LanguageProvider,
  Login,
  ManagePoints,
  MembershipPlans,
  MyBusinessAccount,
  MyCoupons,
  NewUsedCouponsProcessing,
  PointsManager,
  PointsRequestsFetcher,
  PointsRequestsTable,
  PointsTransactions,
  PointsTransactionsTable,
  PromoCodesManager,
  ReferralProgram,
  SignUp,
  ThemeProvider,
  UsedCoupons,
  UserManager,
  WebsiteCoupons,
  EditUser,
  withAdminAuth,
  Premiummembershippage,
  Standardmembership,
  PrivateAdminRoute,
  AwinOffersFetcher,
  // SignupCouponBuilder,
  VerifyEmail,
  EmailManager
} from 'containers';
// New V1 builder wrapper (used for create + edit routes)
import CouponBuilderWrapper from 'containers/CouponBuilderV1/CouponBuilderWrapper';
// New customer-facing SignIn screen, replacing the old CustomerLogin
import CustomerLogin from 'containers/CustomerLogin/CustomerLogin';
import About from 'containers/LandingPage/pages/About';
import LaunchPackage from 'containers/LandingPage/pages/LaunchPackage';
import Page1 from 'containers/LandingPage/pages/Page1';
import Page2 from 'containers/LandingPage/pages/Page2';
import Sitemap from 'containers/LandingPage/pages/Sitemap';
import SignupCouponBuilderWrapper from 'containers/SignupCouponBuilder/SignupCouponBuilderWrapper';
import {initAnalytics} from 'utils/analytics';
import CustomerRoutes from './customerRoutes';
import CustomerLayout from './CustomerLayout';

import CustomerForgotPassword from "../../screens/ForgotPassword/ForgotPassword";
import NotFound from "../../screens/NotFound/NotFound";
import WebsiteBlogs from "../../screens/Blogs/WebsiteBlogs";
import { actions as statsActions } from "coupon-common/src/modules/stats";


export const App = ({ messages, hasDashboardAccess, hasDashboardAccessFetching }) => {
  const [showSplash, setShowSplash] = useState(true);
  const handleSplashComplete = useCallback(() => setShowSplash(false), []);
  const dispatch = useDispatch();

  useEffect(() => {
    initializeBranch();
  }, []);

  useEffect(() => {
    initAnalytics({ dispatch, sendEvents: statsActions.sendEvents });
  }, [dispatch]);

  // if (showSplash) {
  //   return (
  //     <SplashScreen
  //       onComplete={handleSplashComplete}
  //       appReady={!hasDashboardAccessFetching}
  //     />
  //   );
  // }

  return (
    <LanguageProvider messages={messages}>
      <ThemeProvider>
        <NavigationLoadingOverlay />
        <NewUsedCouponsProcessing />
        <PointsRequestsFetcher />
        <ToastContainer hideProgressBar transition={Slide} />
        <ScrollToTop />
        <Routes>
          <Route path="/AuthFlow" element={<AuthFlow />} />
          {/* Public */}
          {/* <Route path="/" element={<Landing />} /> */}
          <Route path="/landing" element={<Page1 />} />
          <Route path="/landing/business" element={<Page2 />} />
          <Route path="/business" element={<Page2 />} />
          <Route path="/launch-moncton" element={<LaunchPackage />} />
          {/* One launch page per location; /launch on its own lands on Moncton. */}
          <Route path="/launch" element={<Navigate to="/launch-moncton" replace />} />
          <Route path="/launch-moncton" element={<LaunchPackage location="Moncton" />} />
          <Route path="/launch-calgary" element={<LaunchPackage location="Calgary" />} />
          <Route path="/launch-canada" element={<LaunchPackage location="Canada" />} />
          {/* Old long URLs keep working (already-shared links, search results). */}
          <Route path="/launch-package" element={<Navigate to="/launch-moncton" replace />} />
          <Route path="/launch-package-moncton" element={<Navigate to="/launch-moncton" replace />} />
          <Route path="/launch-package-calgary" element={<Navigate to="/launch-calgary" replace />} />
          <Route path="/launch-package-canada" element={<Navigate to="/launch-canada" replace />} />
          <Route path="/about" element={<About />} />
          <Route path="/sitemap" element={<Sitemap />} />
          {/* Old landing-navbar URL for Online Brands. */}
          <Route path="/online-businesses" element={<Navigate to="/online-brands-list" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/customer-login" element={<CustomerLogin />} />
          <Route path="/customer-forgot-password" element={<CustomerForgotPassword />} />

          <Route path="/referral-program" element={<ReferralProgram/>}/>

          {/* DEV PREVIEW */}
          <Route path="/dev/signup-coupon-builder" element={<SignupCouponBuilderWrapper />} />
          <Route path="/create-coupon" element={<SignupCouponBuilderWrapper />} />

          <Route path="/signup/*" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
<Route element={<CustomerLayout />}>
  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
  <Route path="/terms-of-service/customer" element={<TermsOfService variant="customer" />} />
</Route>
<Route path="/dealer-agreement" element={<DealerAgreement />} />
<Route path="/terms-of-service" element={<TermsOfService />} />
<Route path="/terms-of-service/:variant" element={<TermsOfService />} />


          <Route path="/verify-email" element={<VerifyEmail />} />


          {/* Admin */}
          {/* Original v5 Route/component definitions, kept for reference during
              the v6 migration — superseded by the <Route element={<PrivateAdminRoute .../>}>
              block below.
          <Route exact path="/admin" component={withAdminAuth(AdminPanel)} />
          <Route
            exact
            path="/admin/points"
            component={withAdminAuth(PointsManager)}
          />
          <Route
            exact
            path="/admin/points/manage"
            component={withAdminAuth(ManagePoints)}
          />
          <Route exact path="/referral-program" component={ReferralProgram} />
          <Route exact path="/membership-plans" component={MembershipPlans} />
          <Route exact path="/coupons/create" component={CouponBuilder} />
          <Route exact path="/coupons" component={MyCoupons} />
          <Route exact path="/website-coupons" component={WebsiteCoupons} />
          <Route exact path="/blogs" component={WebsiteBlogs} />
          <Route exact path="/blogs/:id" component={WebsiteBlogs} />
          <Route exact path="/used-coupons" component={UsedCoupons} />
          <Route exact path="/points-requests" component={PointsRequestsTable} />
          <Route exact path="/points-transactions" component={PointsTransactionsTable} />
          <Route path="/coupons/:id" component={CouponBuilder} />
          <Route path="/signup" component={SignUp} />
          <Route path="/forgot-password" component={ForgotPassword} />
          <Route path="/privacy-policy" component={PrivacyPolicy} />
          <Route path="/dealer-agreement" component={DealerAgreement} />
          <Route path="/terms-of-service/:variant" component={TermsOfService} />
          <Route path="*" render={() => <h2>Not found</h2>} />
        </Switch>
          <Route
            exact
            path="/admin/points/transactions"
            component={withAdminAuth(PointsTransactions)}
          />
          <Route
            exact
            path="/admin/users"
            component={withAdminAuth(UserManager)}
          />
          <Route
            exact
            path="/admin/users/:id"
            component={withAdminAuth(EditUser)}
          />

          <Route exact path="/admin/coupon-import" component={withAdminAuth(CouponImport)} /> */}

          <Route path="/admin" element={<PrivateAdminRoute component={AdminPanel} />} />
          <Route path="/admin/points" element={<PrivateAdminRoute component={PointsManager} />} />
          <Route path="/admin/points/manage" element={<PrivateAdminRoute component={ManagePoints} />} />
          <Route path="/admin/points/transactions" element={<PrivateAdminRoute component={PointsTransactions} />} />
          <Route path="/admin/users" element={<PrivateAdminRoute component={UserManager} />} />
          <Route path="/admin/users/:id" element={<PrivateAdminRoute component={EditUser} />} />
          <Route path="/admin/affiliate-manager" element={<PrivateAdminRoute component={AffiliateBusinessManager} />} />
          <Route path="/admin/flyer-import" element={<PrivateAdminRoute component={FlyerCouponsTest} />} />
          <Route path="/admin/rakuten-import" element={<PrivateAdminRoute component={RakutenCouponFetcher} />} />
          <Route path="/admin/awin-import" element={<PrivateAdminRoute component={AwinOffersFetcher} />} />
          <Route path="/admin/cj-import" element={<PrivateAdminRoute component={CJOfferFetcher} />} />
          <Route path="/admin/impact-import" element={<PrivateAdminRoute component={ImpactOfferFetcher} />} />
          <Route path="/admin/analytics" element={<PrivateAdminRoute component={AnalyticsDashboard} />} />
          <Route path="/admin/affiliate-coupon-viewer" element={<PrivateAdminRoute component={AffiliateCouponViewer} />} />
          <Route path="/admin/blog-manager" element={<PrivateAdminRoute component={BlogManager} />} />
          <Route path="/admin/promo-manager" element={<PrivateAdminRoute component={PromoCodesManager} />} />
          
          <Route path="/admin/email-manager" element={<PrivateAdminRoute component={EmailManager} />} />

        <Route element={<BusinessSessionGuard />}>
          <Route path="/dashboard2" element={<Dashboard2 />} />
          <Route path="/dashboard" element={<Dashboard3 />} />
          <Route path="/coupons" element={<MyCoupons />} />
          <Route path="/coupons/create" element={<CouponBuilderWrapper />} />
          <Route path="/coupons/:id" element={<CouponBuilderWrapper />} />
          <Route path="/business-blogs" element={<><NavBar /><WebsiteBlogs /></>} />
          {/* <Route path="/used-coupons" element={<UsedCoupons />} /> */}
          <Route path="/points-requests" element={<PointsRequestsTable />} />
          <Route path="/points-transactions" element={<PointsTransactionsTable />} />
                    {/* Account */}
          <Route path="/account" element={<MyBusinessAccount />} />
          <Route path="/account/settings" element={<AccountSettings />} />
        </Route>

          <Route path="/customer-coupons" element={<CustomerCouponListAll />} />

          {/* Customer-facing app (Home, Coupons, Businesses, MyAccount,
              Payment, Preferences, UsedCoupons (customer), Referrals,
              customer Privacy Policy / Terms of Service, Blogs, etc.)

              NOTE: v6 ranks routes by specificity, not by source order, so
              this "/*" catch-all correctly yields to every literal path
              above (/admin, /coupons, /dashboard, etc.) even though it's
              listed after some and before others — it only matches when
              nothing more specific did. The final "*" Not-Found route below
              is even less specific and only fires if CustomerRoutes' own
              internal <Routes> also fails to match (which it won't usually
              reach, since CustomerRoutes has no catch-all of its own —
              consider adding one inside CustomerRoutes.jsx for unmatched
              customer paths instead of falling through to this generic
              "Not found"). */}
          <Route path="/*" element={<CustomerRoutes />} />

          {/* Fallback */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ThemeProvider>
    </LanguageProvider>
  );
};

App.propTypes = {
  messages: PropTypes.object,
  hasDashboardAccess: PropTypes.bool,
  hasDashboardAccessFetching: PropTypes.bool,
};

const mapStateToProps = state => ({
  hasDashboardAccess: state.businessReferralProgram.data.has_access_to_dashboard,
  hasDashboardAccessFetching: state.businessReferralProgram.fetching,
});

export default connect(mapStateToProps)(App);